import {ContractInfo} from 'components/contract-info/ContractInfo'
import {connectService} from 'components/service/connectService'
import {useFormik} from 'formik'
import {useConfirm} from 'hooks/useConfirm'
import {Sections} from 'modules/core/tenant/sections/Sections'
import {NavigationContext} from 'modules/dashboard/NavigationContext'
import {useContext, useEffect, useMemo} from 'react'
import {useNavigate} from 'react-router-dom'
import {ContractTypes} from 'utility/utility'
import {DistributorSchema} from './schema'
import {pick} from 'lodash'
import {ValidationError} from 'components/validation-error/ValidationError'

export const Distributor = connectService(props => {
  const confirm = useConfirm()
  const navigate = useNavigate()
  const {setTitle} = useContext(NavigationContext)
  const {edit, data, onSave, remove} = props

  useEffect(() => {
    setTitle(data?.name || 'Create Distributor')
  }, [data.name])

  const handleRemove = async () => {
    await remove(data._id)
    navigate(-1)
  }

  const initialData = useMemo(() => {
    return pick(data, [
      'name',
      'address',
      'postalCode',
      'phone',
      'accountNumber',
      'contractType',
      'trialExpiry',
      'contractExpiry'
    ])
  }, [data, edit])

  const formik = useFormik({
    initialValues: edit
      ? initialData
      : {
          name: '',
          address: '',
          postalCode: '',
          phone: '',
          accountNumber: '',
          contractType: ContractTypes.Trial,
          trialExpiry: '',
          contractExpiry: ''
        },
    validationSchema: DistributorSchema,
    onSubmit: async data => {
      if (data.contractType === ContractTypes.FreePartnerAccount) {
        delete data.contractExpiry
        delete data.trialExpiry
      }

      await onSave({data})
      navigate(-1)
    },
    enableReinitialize: true
  })

  return (
    <>
      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">Distributor name</label>
        <div className="col-sm-8 col-lg-7">
          <input
            type="text"
            placeholder="Distributor name"
            className="form-control"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            autoFocus
          />
          <ValidationError formik={formik} property="name" />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">Address</label>
        <div className="col-sm-8 col-lg-7">
          <input
            type="text"
            className="form-control"
            placeholder="Address"
            name="address"
            value={formik.values.address}
            onChange={formik.handleChange}
          />
          <ValidationError formik={formik} property="address" />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">Postcode</label>
        <div className="col-sm-8 col-lg-7">
          <input
            type="text"
            className="form-control"
            placeholder="Postcode"
            name="postalCode"
            value={formik.values.postalCode}
            onChange={formik.handleChange}
          />
          <ValidationError formik={formik} property="postalCode" />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">Phone Number</label>
        <div className="col-sm-8 col-lg-7">
          <input
            type="phone"
            className="form-control form-control"
            placeholder="Phone number"
            name="phone"
            value={formik.values.phone}
            onChange={formik.handleChange}
          />
          <ValidationError formik={formik} property="phone" />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">Account Number from ZOHO</label>
        <div className="col-sm-8 col-lg-7">
          <input
            type="text"
            placeholder="Account Number from ZOHO"
            className="form-control"
            name="accountNumber"
            value={formik.values.accountNumber}
            onChange={formik.handleChange}
          />
          <ValidationError formik={formik} property="accountNumber" />
        </div>
      </div>

      <ContractInfo
        contractType={formik.values.contractType}
        contractTypeProps={{name: 'contractType'}}
        onContractTypeChange={formik.handleChange}
        trialExpiry={formik.values.trialExpiry}
        onTrialExpiryChange={value => {
          formik.setFieldValue('trialExpiry', value)
          formik.setFieldValue('contractExpiry', undefined)
        }}
        contractExpiry={formik.values.contractExpiry}
        onContractExpiryChange={value => {
          formik.setFieldValue('contractExpiry', value)
          formik.setFieldValue('trialExpiry', undefined)
        }}
      >
        {((formik.touched.contractType && formik.errors.contractType) ||
          (formik.touched.trialExpiry && formik.errors.trialExpiry) ||
          (formik.touched.contractExpiry && formik.errors.contractExpiry)) && (
          <small className="form-text text-danger">
            {formik.errors.accountNumber}
            {formik.errors.trialExpiry}
            {formik.errors.contractExpiry}
          </small>
        )}
      </ContractInfo>

      {edit && (
        <Sections
          AdministratorTabProps={{
            entity: 'distributor'
          }}
        />
      )}

      <div className="vw-popup-box__inner__body__btns">
        <button className="btn btn-primary" onClick={formik.handleSubmit}>
          Save Distributor
        </button>
        {edit && (
          <button
            className="btn btn-danger"
            onClick={() => {
              confirm({
                title: `Are you sure?`,
                message: `Are you sure you want to remove distributor ${data.name}? Once deleted, all linked resellers, business and users will be removed as well. This step can not be undo.`,
                onConfirm: handleRemove
              })
            }}
          >
            Remove Distributor
          </button>
        )}
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Cancel
        </button>
      </div>
    </>
  )
}, 'distributor')
